import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormField,
  Multiselect,
  MultiselectProps,
  RadioGroup,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OmsReport } from "../../lusca-search-api/generated-src";
import OMSColumnOrder from "./OMSColumnOrder";

export type OmsColumnsDraft = {
  strategy: "all" | "selected";
  names: string[];
  touched?: boolean;
};

export function columnSelectionError(
  settings: OmsColumnsDraft,
  report: OmsReport
): string | undefined {
  if (settings.strategy === "all") return undefined;
  if (!settings.names.length) return "Choose at least one column.";
  const missing = settings.names.filter(
    (name) => !report.columns.includes(name)
  );
  if (missing.length)
    return `Unavailable in this source: ${missing.slice(0, 3).join(", ")}${
      missing.length > 3 ? ` and ${missing.length - 3} more` : ""
    }. Remove or replace these columns.`;
  if (new Set(settings.names).size !== settings.names.length)
    return "Remove duplicate output columns.";
  return undefined;
}

const typeLabels = {
  string: "Text",
  number: "Number",
  date: "Date",
  datetime: "Date and time",
};

export default function OMSColumns({
  report,
  settings,
  onChange,
  focusRequested = false,
  onFocusHandled,
}: {
  report: OmsReport;
  settings: OmsColumnsDraft;
  onChange: (value: OmsColumnsDraft) => void;
  focusRequested?: boolean;
  onFocusHandled?: () => void;
}) {
  const picker = useRef<MultiselectProps.Ref>(null);
  const pickerContainer = useRef<HTMLDivElement>(null);
  const [openPicker, setOpenPicker] = useState(false);
  const error = columnSelectionError(settings, report);
  const visibleError =
    error && (settings.touched || settings.names.length > 0)
      ? error
      : undefined;
  useEffect(() => {
    if (!focusRequested && !openPicker) return undefined;
    const timer = window.setTimeout(() => {
      picker.current?.focus();
      const trigger = pickerContainer.current?.querySelector<HTMLButtonElement>(
        'button[aria-haspopup="dialog"], button[aria-haspopup="listbox"]'
      );
      if (openPicker && trigger?.getAttribute("aria-expanded") !== "true")
        trigger?.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
      setOpenPicker(false);
      onFocusHandled?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [focusRequested, openPicker, onFocusHandled]);

  const changeNames = useCallback(
    (names: string[]) => {
      onChange({ ...settings, names, touched: true });
    },
    [onChange, settings]
  );
  const focusPicker = useCallback(() => {
    picker.current?.focus();
  }, []);
  return (
    <div className="oms-columns__layout">
      <div className="oms-columns__selection">
        <FormField
          label="Columns to export"
          constraintText="All columns includes newly added fields."
        >
          <RadioGroup
            direction="horizontal"
            value={settings.strategy}
            items={[
              { value: "all", label: "All columns" },
              { value: "selected", label: "Selected columns" },
            ]}
            onChange={({ detail }) => {
              const strategy = detail.value as OmsColumnsDraft["strategy"];
              onChange({ ...settings, strategy });
              if (strategy === "selected") setOpenPicker(true);
            }}
          />
        </FormField>
        {settings.strategy === "all" && (
          <Box color="text-body-secondary">
            All {report.columns.length} columns in the report&apos;s original
            order.
          </Box>
        )}
        {settings.strategy === "selected" && (
          <div className="oms-columns__picker-controls">
            <FormField
              label="Column selection"
              errorText={visibleError}
              constraintText={
                !visibleError && !settings.names.length
                  ? "Choose at least one column."
                  : undefined
              }
            >
              <div ref={pickerContainer}>
                <Multiselect
                  expandToViewport
                  ref={picker}
                  ariaLabel="Output columns"
                  filteringType="auto"
                  filteringPlaceholder="Search available columns"
                  filteringAriaLabel="Search available columns"
                  filteringClearAriaLabel="Clear available column search"
                  placeholder={
                    settings.names.length
                      ? "Add or remove columns"
                      : "Choose columns"
                  }
                  empty="This report has no available columns."
                  noMatch="No matching columns."
                  keepOpen
                  hideTokens
                  selectedOptions={settings.names.map((name) => ({
                    label: name,
                    value: name,
                  }))}
                  options={[...report.columns]
                    .sort((a, b) => a.localeCompare(b))
                    .map((name) => ({
                      label: name,
                      value: name,
                      description:
                        typeLabels[report.columnTypes?.[name] || "string"],
                    }))}
                  deselectAriaLabel={(option) =>
                    `Remove column ${option.label || option.value || "column"}`
                  }
                  onChange={({ detail }) =>
                    changeNames(
                      detail.selectedOptions.map(
                        (option) => option.value as string
                      )
                    )
                  }
                />
              </div>
            </FormField>
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => changeNames([...report.columns])}>
                Select all columns
              </Button>
              <Button
                disabled={!settings.names.length}
                onClick={() => {
                  changeNames([]);
                }}
              >
                Clear column selection
              </Button>
            </SpaceBetween>
          </div>
        )}
      </div>
      {settings.strategy === "selected" && settings.names.length > 0 && (
        <OMSColumnOrder
          names={settings.names}
          reportOrder={report.columns}
          onChange={changeNames}
          onEmpty={focusPicker}
        />
      )}
    </div>
  );
}
